import User, { UserCredentials } from "../models/user/User";
import UserProfile from "../models/user/UserProfile";
import { Token, RefreshToken } from "../models/auth/Token";
import {
  EXPENSES_TRACKER_LOGIN_URL,
  EXPENSES_TRACKER_LOGOUT_ALL_URL,
  EXPENSES_TRACKER_LOGOUT_URL,
  EXPENSES_TRACKER_REFRESH_TOKEN_URL,
  EXPENSES_TRACKER_REGISTER_URL,
  EXPENSES_TRACKER_RESEND_VERIFICATION_EMAIL_URL,
  EXPENSES_TRACKER_VERIFY_EMAIL_URL,
  PASSWORD_RESET_REQUEST_URL,
  PASSWORD_RESET_RESET_URL,
} from "../utils/constants/ApiConstants";
import { httpClient } from "./http/httpClient";
import { setTokens, getTokens, clearTokens } from "../auth/tokenUtils";

export async function login(
  email: string,
  password: string,
  extendedSession: boolean
): Promise<{ emailVerified: boolean; email: string }> {
  const userCredentials: UserCredentials = { email, password };
  type LoginResponse = Token & { emailVerified?: boolean; email?: string };
  const { data } = await httpClient.public.post<LoginResponse, any>(
    EXPENSES_TRACKER_LOGIN_URL,
    { ...userCredentials, extendedSession }
  );
  setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken }, { persistent: extendedSession });
  return {
    emailVerified: Boolean(data.emailVerified),
    email: String(data.email || email),
  };
}

export async function refreshToken(): Promise<void> {
  const tokens = getTokens();
  if (!tokens?.refreshToken) {
    throw new Error("No refresh token available");
  }
  const refreshTokenRequest: RefreshToken = { refreshToken: tokens.refreshToken };
  const { data } = await httpClient.public.post<Token, RefreshToken>(
    EXPENSES_TRACKER_REFRESH_TOKEN_URL,
    refreshTokenRequest
  );
  setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
}

export async function register(
  email: string,
  password: string,
  name: string,
  currency: string,
  verificationCallbackUrl: string
): Promise<void> {
  const userCredentials: UserCredentials = { email, password };
  const userProfile: UserProfile = { email, name, currency, startingBalance: 0.0 };
  const user: User = { user: userCredentials, userProfile };
  await httpClient.public.post<void, any>(
    EXPENSES_TRACKER_REGISTER_URL,
    { ...user, verificationCallbackUrl }
  );
}

export async function logout(): Promise<void> {
  const tokens = getTokens();
  const refreshToken = tokens?.refreshToken;
  if (refreshToken) {
    const refreshTokenRequest: RefreshToken = { refreshToken };
    await httpClient.public.post<void, RefreshToken>(
      EXPENSES_TRACKER_LOGOUT_URL,
      refreshTokenRequest
    );
  }
  clearTokens();
}

export async function logoutAll(): Promise<void> {
  const tokens = getTokens();
  const refreshToken = tokens?.refreshToken;
  if (refreshToken) {
    const refreshTokenRequest: RefreshToken = { refreshToken };
    await httpClient.public.post<void, RefreshToken>(
      EXPENSES_TRACKER_LOGOUT_ALL_URL,
      refreshTokenRequest
    );
  }
  clearTokens();
}

export async function verifyEmail(token: string): Promise<void> {
  await httpClient.public.get<void>(EXPENSES_TRACKER_VERIFY_EMAIL_URL, { query: { token } });
}

export async function resendVerificationEmail(email: string, verificationCallbackUrl: string): Promise<void> {
  await httpClient.public.post<void, { email: string; verificationCallbackUrl: string }>(
    EXPENSES_TRACKER_RESEND_VERIFICATION_EMAIL_URL,
    { email, verificationCallbackUrl }
  );
}

export async function requestPasswordReset(email: string, callbackUrl: string): Promise<void> {
  await httpClient.public.post<void, { email: string; callbackUrl: string }>(
    PASSWORD_RESET_REQUEST_URL,
    { email, callbackUrl }
  );
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await httpClient.public.post<void, { token: string; newPassword: string }>(
    PASSWORD_RESET_RESET_URL,
    { token, newPassword }
  );
}


